import {
  Container,
  Input,
  ProgressiveReading,
  Typography
} from '@evocolabs/orkaui'
import {
  FAQEntryWithFeedback,
  MoreQuestions,
  SuccessModal
} from './FAQUtils'
import React, { useEffect, useState } from 'react'
import {
  highlightKeywords,
  structuralFaqData
} from '@/src/components/FAQ/FAQUtils'

import { Box } from '@mui/material'
import ChevronDownDouble24 from '@evocolabs/icons/lib/ChevronDownDouble24'
import Close24 from '@evocolabs/icons/lib/Close24'
import { Divider } from '@mui/material'
import Fuse from 'fuse.js'
import { InView } from 'react-intersection-observer' //useInview deleted
import { NextSeo } from 'next-seo'
import Search24 from '@evocolabs/icons/lib/Search24'
import SearchResult120 from '@evocolabs/icons/lib/SearchResult120'
import styles from '@/pages/scss/FAQ.module.scss'
import theme from '@/src/theme'

function FAQHeader({
  searchInput,
  setSearchInput
}: {
  searchInput: string
  setSearchInput: (input: string) => void
}) {
  const handleInputChange = (e) => {
    setSearchInput(e.target.value)
  }

  const clearInput = () => {
    setSearchInput('')
  }

  const clearIcon = (
    <>
      <div onClick={clearInput} className={styles['closeIcon']}>
        <Close24 />
      </div>
      {!!searchInput && (
        <div
          onClick={() => {
            clearInput()
          }}
          className={styles.cancelButton}
        >
          <Typography variant="Body2" textAlign="center">
            Cancel
          </Typography>
        </div>
      )}
    </>
  )

  return (
    <div className={styles['faq-header-wrapper']}>
      <div>
        <Typography variant="h1" textAlign="center" color="#ffffff">
          FAQ
        </Typography>
        <Typography
          variant="subh1"
          textAlign="center"
          color="#ffffff"
          className={styles['faq-subtitle']}
        >
          Everything you need to know about Orka
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          marginTop="20px"
        >
          <Input
            variant="filled"
            value={searchInput}
            className={styles.searchBoxPC}
            placeholder="Search for questions"
            icon={<Search24 />}
            onChange={handleInputChange}
            endAdornment={!!searchInput && clearIcon}
            id="input_faq-search"
          />
        </Box>
      </div>
    </div>
  )
}

function Faq({ faqInfo }: { faqInfo: any }) {
  const faqData: any = structuralFaqData(faqInfo)
  const commonQuestionsData = faqInfo.results.data
    .filter((d) => d.is_common)
    .sort((a, b) => b.frequency - a.frequency)
  faqData['Common Questions'] = commonQuestionsData
  faqData.sections.unshift('Common Questions')
  const [curSection, setCurSection] = useState(faqData.sections[0])
  const [isNavigating, setIsNavigating] = useState(false)

  // const [ref, inView] = useInView({
  //   threshold: 0.5, // Adjust the threshold as needed (0.5 means 50% visibility)
  //   triggerOnce: true // Only trigger once when the element enters the viewport
  // })
  const [sucModalOpen, setSucModalOpen] = useState(false)
  const [expandedList, setExpandedList] = useState(
    Array(faqData.sections.length).fill('')
  )

  const catalogNavigator = (sectionName) => {
    setIsNavigating(true)
    setCurSection(sectionName)
    const ele = document.getElementById(sectionName.replace(/[\W_]+/g, '_'))
    try {
      ele.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    } catch {
      ele.scrollIntoView(true)
    } finally {
      setTimeout(() => setIsNavigating(false), 2000)
    }
  }
  return (
    <Container id="qa-container">
      <div className={styles['grid-flex']}>
        <div className={styles['section-grid']}>
          <Box sx={{ marginBottom: '16px' }}>
            <Typography variant="Body2" sx={{ color: theme.palette.grey[500] }}>
              Browse by topic
            </Typography>
          </Box>

          {faqData.sections.map((v, i) => (
            <a
              key={i}
              onClick={() => catalogNavigator(v)}
              className={
                v === curSection
                  ? styles['selected-section']
                  : styles['ordinary-section']
              }
            >
              <Typography
                variant="Body2"
                className={[
                  v === curSection ? styles['section-selected-text'] : ''
                ].join(' ')}
              >
                {v}
              </Typography>
              <ChevronDownDouble24 />
            </a>
          ))}
          <Box sx={{ width: '249px' }} />
          <SuccessModal
            modalOpen={sucModalOpen}
            setModalOpen={setSucModalOpen}
          />
        </div>
        <div />
        <div className={styles['question-grid']}>
          {faqData.sections.map((sectionItem, index) => (
            <InView
              key={index}
              as="div"
              rootMargin="-50% 100% -10% 100%"
              threshold={0.1}
              onChange={(inView) => {
                if (inView && !isNavigating) setCurSection(sectionItem)
              }}
            >
              <Box
                paddingTop={index !== 0 && '64px'}
                className={styles['subsection-wrapper']}
                key={sectionItem.replace(/[\W_]+/g, '_')}
              >
                <Typography
                  variant="h3"
                  id={sectionItem.replace(/[\W_]+/g, '_')}
                >
                  {sectionItem}
                </Typography>
                <Divider
                  sx={{ mt: 3, backgroundColor: '#9E8B70', height: '3px' }}
                />

                <ProgressiveReading
                  key={sectionItem.replace(/[\W_]+/g, '_')}
                  content={faqData[sectionItem].map((d, idx) => {
                    const clickHandler = (panel) => (event, isExpanded) => {
                      const newExpandedList = expandedList.concat()
                      newExpandedList[index] = isExpanded ? panel : ''
                      setExpandedList(newExpandedList)
                    }
                    return {
                      question: d.question,
                      answer: <FAQEntryWithFeedback data={d} />,
                      id: d.question.replace(/[\W_]+/g, '_'),
                      expanded: expandedList[index] === idx,
                      setExpansion: clickHandler(idx)
                    }
                  })}
                />
              </Box>
            </InView>
          ))}
        </div>
      </div>
    </Container>
  )
}

function SearchPagePC({
  searchInput,
  searchResult
}: {
  searchInput: string
  searchResult: any[]
}) {
  const keywords = searchInput.split(' ')

  return searchResult.length !== 0 ? (
    <Container id="qa-container">
      <Typography variant="h4">
        {searchResult.length} results for "{searchInput}"
      </Typography>
      <div />
      <div className={styles['question-grid']}>
        <ProgressiveReading
          key={'searchResult'}
          content={searchResult.map((d) => {
            return {
              question: highlightKeywords(d.question, keywords),
              answer: d.content,
              id: d.id
            }
          })}
          hasLastDivider
        />
      </div>
      <Box sx={{ width: '600px', mt: '-40px' }}>
        <MoreQuestions section_name={'search'} />
      </Box>
    </Container>
  ) : (
    <Box className={styles.noResultWrapper}>
      <SearchResult120 />
      <Typography variant="Body1">No Results for "{searchInput}"</Typography>
      <Box sx={{ margin: 'auto', width: '48%', marginBottom: '64px' }}>
        <MoreQuestions section_name={'search'} />
      </Box>
    </Box>
  )
}

const FAQPC = ({ data }: { data: any }) => {
  const [searchInput, setSearchInput] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const performSearch = (term) => {
    const options = {
      includeScore: true,
      ignoreLocation: true,
      keys: ['question', 'content', 'section'],
      threshold: 0.15
    }
    const fuse = new Fuse(data.results.data, options)
    const results = fuse.search(term).map((result) => {
      return {
        question: result.item['question'],
        content: <FAQEntryWithFeedback data={result.item} />,
        id: result.item['id'],
        link: `/FAQ/qst/${result.item['id']}?frompage=search`
      }
    })

    setSearchResult(results)
  }

  useEffect(() => {
    if (searchInput.trim()) {
      performSearch(searchInput)
    } else {
      setSearchResult([])
    }
  }, [searchInput])

  return (
    <>
      <NextSeo
        title="Frequently Asked Questions | Orka Hearing Aids"
        description="Explore Orka’s FAQ page for quick answers on Orka, Orka hearing aids, hearing loss, and more."
      />
      <FAQHeader searchInput={searchInput} setSearchInput={setSearchInput} />
      {searchInput ? (
        <SearchPagePC searchResult={searchResult} searchInput={searchInput} />
      ) : (
        <Faq faqInfo={data} />
      )}
    </>
  )
}

export default FAQPC
